// @ts-nocheck
/**
 * Product Builder — unified flow lock (recording, future upload/AI).
 * Single source of truth for "can the customer navigate away from the active step?"
 *
 * Contract:
 * - Field plugins / audio engine acquire + release tokens on lifecycle only
 * - PBNavigation + handleNextClick are the ONLY navigation enforcers
 * - Validation/footer chrome must NOT treat locks as field validity
 */
(function () {
  'use strict';

  var LOCKS = Object.freeze({
    RECORDING: 'recording',
    GENERATION: 'generation',
  });

  var GENERATION_LEAVE_MSG = 'Finish or cancel character creation before leaving.';

  var locks = [];
  var tokenSeq = 0;

  function isDetachedScope(el) {
    if (!el || typeof el !== 'object') return false;
    // Explicit signal from real DOM nodes (and test stubs that set it).
    if (typeof el.isConnected === 'boolean') return !el.isConnected;
    // Only run contains() checks on real Node-like objects — plain {id} stubs are connected.
    if (typeof el.nodeType !== 'number') return false;
    try {
      return !(el.ownerDocument && el.ownerDocument.documentElement && el.ownerDocument.documentElement.contains(el));
    } catch (e) {
      return false;
    }
  }

  function pruneStaleLocks() {
    var changed = false;
    for (var i = locks.length - 1; i >= 0; i--) {
      var scope = locks[i].scopeEl;
      if (scope && isDetachedScope(scope)) {
        locks.splice(i, 1);
        changed = true;
      }
    }
    if (changed) notifyFlowLockChange();
    return changed;
  }

  function notifyFlowLockChange() {
    try {
      document.dispatchEvent(new CustomEvent('pb-flow-lock-change', { bubbles: true }));
    } catch (e) {}
    scheduleFooterRefresh();
  }

  function scheduleFooterRefresh() {
    if (typeof window !== 'undefined' && window.PBFooterNav && typeof window.PBFooterNav.scheduleSync === 'function') {
      window.PBFooterNav.scheduleSync();
      return;
    }
    var schedule =
      typeof queueMicrotask === 'function'
        ? queueMicrotask
        : function (fn) {
            setTimeout(fn, 0);
          };
    schedule(function () {
      if (
        typeof window !== 'undefined' &&
        window.productBuilder &&
        typeof window.productBuilder.checkStepValidity === 'function'
      ) {
        window.productBuilder.checkStepValidity();
      }
    });
  }

  function acquire(kind, options) {
    options = options || {};
    pruneStaleLocks();
    var entry = {
      kind: kind,
      token: 'pbfl-' + ++tokenSeq,
      scopeEl: options.scopeEl || null,
    };
    locks.push(entry);
    notifyFlowLockChange();
    return entry.token;
  }

  function release(token) {
    if (!token) return false;
    var idx = -1;
    for (var i = 0; i < locks.length; i++) {
      if (locks[i].token === token) {
        idx = i;
        break;
      }
    }
    if (idx === -1) return false;
    locks.splice(idx, 1);
    notifyFlowLockChange();
    return true;
  }

  function releaseAll(kind) {
    var changed = false;
    for (var i = locks.length - 1; i >= 0; i--) {
      if (!kind || locks[i].kind === kind) {
        locks.splice(i, 1);
        changed = true;
      }
    }
    if (changed) notifyFlowLockChange();
    return changed;
  }

  function hasActiveLocks(kind) {
    pruneStaleLocks();
    for (var i = 0; i < locks.length; i++) {
      if (!kind || locks[i].kind === kind) return true;
    }
    return false;
  }

  function isLockActiveIn(container, kind) {
    if (!hasActiveLocks(kind)) return false;
    for (var i = 0; i < locks.length; i++) {
      var lock = locks[i];
      if (kind && lock.kind !== kind) continue;
      var scope = lock.scopeEl;
      if (!container) return true;
      if (!scope || container === scope || container.contains(scope)) return true;
    }
    return false;
  }

  function resolveActiveStepElement() {
    if (typeof document === 'undefined' || typeof window === 'undefined') return null;
    var s = window.builderState;
    if (!s) return null;
    var isMain = s.mode === 'MAIN';
    var containerID = isMain ? 'main-flow-container' : 'sub-flow-container';
    var prefix = isMain ? '' : 'sub-';
    var ctx = isMain ? s.main : s.sub;
    if (!ctx || ctx.step == null) return null;
    var root = document.getElementById(containerID);
    if (!root) return null;
    var byId = root.querySelector('#' + prefix + 'content-page-' + ctx.step);
    if (byId) return byId;
    return root.querySelector('.pb-bottom-wrapper .pb-step.active');
  }

  function resolveActiveFlowRoot() {
    if (typeof document === 'undefined' || typeof window === 'undefined') return null;
    var s = window.builderState;
    if (!s) return null;
    var containerID = s.mode === 'MAIN' ? 'main-flow-container' : 'sub-flow-container';
    return document.getElementById(containerID);
  }

  function blocksGenerationSubLeave(navOpts) {
    if (!hasActiveLocks(LOCKS.GENERATION)) return false;
    var s = typeof window !== 'undefined' ? window.builderState : null;
    if (!s || s.mode !== 'SUB') return false;
    if (
      typeof window !== 'undefined' &&
      window.PBAiSessionGate &&
      typeof window.PBAiSessionGate.blocksSubGenerationNavigation === 'function'
    ) {
      return window.PBAiSessionGate.blocksSubGenerationNavigation(s, navOpts || {});
    }
    return true;
  }

  function blocksActiveStepNavigation(navOpts) {
    pruneStaleLocks();
    if (hasActiveLocks(LOCKS.RECORDING)) {
      var step = resolveActiveStepElement();
      var flowRoot = resolveActiveFlowRoot();
      var scope = step || flowRoot;
      if (!scope) {
        /* After prune, remaining locks are live — block while recording even if the
           active step node is briefly missing. Detached scopes were already dropped. */
        return true;
      }
      if (isLockActiveIn(scope, LOCKS.RECORDING)) return true;
    }
    return blocksGenerationSubLeave(navOpts);
  }

  function canNavigate(navOpts) {
    return !blocksActiveStepNavigation(navOpts);
  }

  function getBlockReason(navOpts) {
    pruneStaleLocks();
    if (hasActiveLocks(LOCKS.RECORDING)) {
      var step = resolveActiveStepElement();
      var flowRoot = resolveActiveFlowRoot();
      var scope = step || flowRoot;
      if (!scope || isLockActiveIn(scope, LOCKS.RECORDING)) {
        return {
          kind: LOCKS.RECORDING,
          message: 'Stop or cancel your recording first.',
        };
      }
    }
    if (blocksGenerationSubLeave(navOpts)) {
      return {
        kind: LOCKS.GENERATION,
        message: GENERATION_LEAVE_MSG,
      };
    }
    return null;
  }

  function discardActiveRecordingsOnClose() {
    try {
      if (window.pbAudioRecorder && typeof window.pbAudioRecorder.cancelAllActiveRecordings === 'function') {
        window.pbAudioRecorder.cancelAllActiveRecordings();
      }
      if (window.pbLongTextRecorder && typeof window.pbLongTextRecorder.cancelAllActiveRecordings === 'function') {
        window.pbLongTextRecorder.cancelAllActiveRecordings();
      }
    } catch (e) {}
    releaseAll(LOCKS.RECORDING);
    if (typeof window !== 'undefined' && window.PBFooterNav) {
      if (typeof window.PBFooterNav.clearBlockHint === 'function') window.PBFooterNav.clearBlockHint();
      if (typeof window.PBFooterNav.scheduleSync === 'function') window.PBFooterNav.scheduleSync();
    }
  }

  window.PBFlowLock = {
    LOCKS: LOCKS,
    acquire: acquire,
    release: release,
    releaseAll: releaseAll,
    pruneStaleLocks: pruneStaleLocks,
    hasActiveLocks: hasActiveLocks,
    isLockActiveIn: isLockActiveIn,
    blocksActiveStepNavigation: blocksActiveStepNavigation,
    canNavigate: canNavigate,
    getBlockReason: getBlockReason,
    discardActiveRecordingsOnClose: discardActiveRecordingsOnClose,
    resolveActiveStepElement: resolveActiveStepElement,
    resolveActiveFlowRoot: resolveActiveFlowRoot,
  };
})();
